import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "./use-store"
import {
  setSelectedBrandID,
  clearSelectedBrandID,
  selectSelectedBrandID,
  selectMergedThreads,
  setMergedThreads,
  setBrandOptions,
  currentFilter,
  searchQuery as searchQueryStored,
  selectHasMore,
  setHasMore,
  appendMergedThreads,
  selectDateRangePreset,
  selectCustomDateStart,
  selectCustomDateEnd
} from "../../redux/slices/conversations"
import { useGetThreadsQuery, useLazyGetThreadsQuery } from "../../redux/queries"
import { useUserLastActivity } from "./use-user-last-activity"
import { FilterID, FilterIDs, GetThreadsParams, Threads } from "../../types"
import { useUserRoles } from "./use-user-roles"
import {
  generateBrandOptions,
  initialMsgNotReplied,
  mergeThreads
} from "../utils"
import { dateRangeToTimestamps } from "../utils/datetime"

const THREADS_LIMIT = 250

export const useConversations = () => {
  const dispatch = useAppDispatch()
  const lastActivity = useUserLastActivity()
  const location = useLocation()
  const isSmsView = location.pathname.startsWith("/conversations/sms")
  const isNonSmsView = !isSmsView
  const selectedBrandID = useAppSelector(selectSelectedBrandID)
  const mergedThreads = useAppSelector(selectMergedThreads)
  const searchQuery = useAppSelector(searchQueryStored)
  const currentConversationFilter = useAppSelector(currentFilter)
  const hasMore = useAppSelector(selectHasMore)
  const dateRangePreset = useAppSelector(selectDateRangePreset)
  const customDateStart = useAppSelector(selectCustomDateStart)
  const customDateEnd = useAppSelector(selectCustomDateEnd)
  const { isBankAdmin, isSuperAdmin, user } = useUserRoles()

  const [isLoadingQueryChange, setIsLoadingQueryChange] =
    useState<boolean>(true)
  const [playNewMessageSound, setPlayNewMessageSound] = useState<boolean>(false)

  // Store the last timestamp for API calls
  const lastCallTimestamp = useRef<number>()

  // Track whether active search is present — search uses a different fetch
  // strategy (lazy query, no polling) to avoid duplicate calls
  const isSearchActive = searchQuery.trim().length > 0

  // Handle setting or clearing selectedBrandID based on user roles
  useEffect(() => {
    if (isSuperAdmin) {
      dispatch(clearSelectedBrandID()) // Clear brand selection for super admins
    } else {
      dispatch(setSelectedBrandID(Number(user?.brand_id))) // Set brand for bank admins
    }
  }, [dispatch, isSuperAdmin, user?.brand_id])

  useEffect(() => {
    setIsLoadingQueryChange(true)
    lastCallTimestamp.current = undefined
  }, [selectedBrandID])

  // Reset on date range change
  useEffect(() => {
    setIsLoadingQueryChange(true)
    lastCallTimestamp.current = undefined
  }, [dateRangePreset, customDateStart, customDateEnd])

  // Build base query params (shared between polling query and loadMore)
  const buildBaseParams = useCallback((): GetThreadsParams => {
    const params: GetThreadsParams = {
      message_preview: "true",
      limit: THREADS_LIMIT
    }

    if (isSuperAdmin) {
      params.brand_id = selectedBrandID ?? undefined
    } else if (isBankAdmin) {
      params.brand_id = user?.brand_id ?? undefined
    } else {
      params.loid = user?.id
    }

    // Add date range filters
    const { min: updated_min } = dateRangeToTimestamps(
      dateRangePreset,
      customDateStart ? new Date(customDateStart) : null,
      customDateEnd ? new Date(customDateEnd) : null,
      { unit: "milliseconds" }
    )
    if (updated_min) params.updated_min = updated_min

    return params
  }, [
    isSuperAdmin,
    isBankAdmin,
    selectedBrandID,
    user?.brand_id,
    user?.id,
    dateRangePreset,
    customDateStart,
    customDateEnd
  ])

  // Polling query — only active when there's NO search query
  const queryParam = useMemo(() => {
    const params = buildBaseParams()

    // Only add `lastCallTimestamp` if it's not the first call after brand/date change
    if (!isLoadingQueryChange && lastCallTimestamp.current) {
      params.updated_min = lastCallTimestamp.current
    }

    return params
  }, [buildBaseParams, isLoadingQueryChange])

  const {
    data: threads = [],
    error,
    isError,
    isFetching,
    isLoading,
    isSuccess
  } = useGetThreadsQuery(queryParam, {
    pollingInterval: 10000,
    skip: isNonSmsView || isSearchActive || Date.now() - lastActivity > 180000
  })

  // Search query — fires a single lazy query when searchQuery changes
  const [triggerSearch, { isFetching: isSearchFetching }] =
    useLazyGetThreadsQuery()

  // Use refs to avoid triggering the search effect from callback identity changes
  const buildBaseParamsRef = useRef(buildBaseParams)
  buildBaseParamsRef.current = buildBaseParams
  const triggerSearchRef = useRef(triggerSearch)
  triggerSearchRef.current = triggerSearch

  useEffect(() => {
    const trimmed = searchQuery.trim()
    if (!trimmed) return

    const params = buildBaseParamsRef.current()
    params.name_like = trimmed

    triggerSearchRef
      .current(params)
      .unwrap()
      .then(result => {
        dispatch(setMergedThreads(result))
        dispatch(setHasMore(result.length >= THREADS_LIMIT))
      })
      .catch(() => {
        // Error handled by RTK Query
      })
  }, [searchQuery, dispatch])

  // When search is cleared, restore normal flow
  const prevSearchQuery = useRef(searchQuery)
  useEffect(() => {
    if (prevSearchQuery.current && !searchQuery.trim()) {
      // Search was just cleared — trigger a fresh full load
      setIsLoadingQueryChange(true)
      lastCallTimestamp.current = undefined
    }
    prevSearchQuery.current = searchQuery
  }, [searchQuery])

  // Load More
  const [triggerLoadMore, { isFetching: isLoadingMore }] =
    useLazyGetThreadsQuery()

  const loadMore = useCallback(async () => {
    const params = buildBaseParams()
    params.skip = mergedThreads.length

    const trimmed = searchQuery.trim()
    if (trimmed) params.name_like = trimmed

    try {
      const result = await triggerLoadMore(params).unwrap()
      dispatch(appendMergedThreads(result))
      dispatch(setHasMore(result.length >= THREADS_LIMIT))
    } catch {
      // Error handled by RTK Query
    }
  }, [
    buildBaseParams,
    mergedThreads.length,
    searchQuery,
    triggerLoadMore,
    dispatch
  ])

  // Merge threads whenever there's a change in `threads` or `mergedThreads`
  // Only runs when search is NOT active (polling flow)
  useEffect(() => {
    if (isSearchActive) return

    // Handle the case where fetching completed with zero results
    if (
      !isFetching &&
      isLoadingQueryChange &&
      threads.length === 0 &&
      mergedThreads.length === 0
    ) {
      lastCallTimestamp.current = Date.now()
      setIsLoadingQueryChange(false)
      dispatch(setHasMore(false))
      return
    }

    if (threads.length > 0 || mergedThreads.length > 0) {
      // Reset flag after the first call after brand change
      if (!isFetching && isLoadingQueryChange) {
        lastCallTimestamp.current = Date.now()
        setIsLoadingQueryChange(false)

        dispatch(setMergedThreads(threads))
        dispatch(setHasMore(threads.length >= THREADS_LIMIT))
        return
      }

      const { merged, hasNewMessages } = mergeThreads(mergedThreads, threads)

      // Only dispatch merged threads if they have changed
      if (JSON.stringify(merged) !== JSON.stringify(mergedThreads)) {
        dispatch(setMergedThreads(merged))
      }

      // Only update playNewMessageSound if it has changed
      if (hasNewMessages !== playNewMessageSound) {
        setPlayNewMessageSound(hasNewMessages)
      }
    }
  }, [
    threads,
    mergedThreads,
    dispatch,
    playNewMessageSound,
    isFetching,
    isLoadingQueryChange,
    isSearchActive
  ])

  // Generate brand options based on merged threads
  useEffect(() => {
    const brandOptions = generateBrandOptions(mergedThreads)
    dispatch(setBrandOptions(brandOptions))
  }, [mergedThreads, dispatch])

  const filteredThreads = useMemo(() => {
    const filterByStatusAndEvents = (
      filters: FilterIDs,
      threadsToFilter: Threads
    ) => {
      return threadsToFilter?.filter(thread => {
        const isNotReplied = initialMsgNotReplied(thread)

        return (
          filters.includes(thread.status as FilterID) ||
          thread.events.some(event =>
            filters.includes(event.type as FilterID)
          ) ||
          (isNotReplied && filters.includes("not_replied")) ||
          (!isNotReplied && filters.includes("replied"))
        )
      })
    }

    let filtered = mergedThreads || []

    if (currentConversationFilter && currentConversationFilter.length) {
      filtered =
        filterByStatusAndEvents(currentConversationFilter, filtered) ?? []
    }

    return filtered
  }, [mergedThreads, currentConversationFilter])

  return {
    filteredThreads,
    playNewMessageSound,
    threads: mergedThreads,
    error,
    isSuccess,
    isError,
    isFetching: isFetching || isSearchFetching,
    isLoading,
    isLoadingQueryChange: isLoadingQueryChange && !isSearchActive,
    hasMore,
    loadMore,
    isLoadingMore
  }
}
